import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CHOICES,
  GAME_CONFIG,
  sanitizeCustomQuestion,
  type Choice,
  type RoomState,
} from '@majority-report/shared';
import { useGame } from '../net/GameProvider';
import { useCountdown } from '../hooks/useCountdown';
import { CountdownRing } from '../components/CountdownRing';
import { Button, Card, Heading, Input, Label, Pill, Row, Stack, Subtle } from '../components/ui';
import { pop, wobble } from '../styles/GlobalStyle';

const Pencil = styled.div`
  font-size: clamp(2.6rem, 10vw, 3.6rem);
  text-align: center;
  line-height: 1;
  animation: ${wobble} 3s ease-in-out infinite;
  transform-origin: 50% 90%;
`;

const OptionRow = styled.div`
  animation: ${pop} 260ms ${({ theme }) => theme.transitions.bounce} both;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Letter = styled.span<{ $choice: Choice }>`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  font-weight: 800;
  background: ${({ theme, $choice }) => theme.choice[$choice].base};
  color: ${({ theme, $choice }) => theme.choice[$choice].ink};
`;

const Counter = styled.span<{ $over: boolean }>`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${({ theme, $over }) => ($over ? theme.colors.danger : theme.colors.inkFaint)};
`;

const WaitingCard = styled(Card)`
  text-align: center;
`;

const Dots = styled.div`
  font-size: 2.4rem;
  animation: ${wobble} 1.6s ease-in-out infinite;
`;

/**
 * Custom mode, phase 0: one player composes the question everyone else will
 * answer. Everybody else gets a holding screen with the same countdown.
 */
export function WritingScreen({ state }: { state: RoomState }) {
  const { t } = useTranslation();
  const { actions, playerId } = useGame();
  const countdown = useCountdown(state.deadline, GAME_CONFIG.writeSeconds * 1000);

  const isAuthor = state.authorId === playerId;
  const author = state.players.find((p) => p.id === state.authorId);

  const [prompt, setPrompt] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [submitted, setSubmitted] = useState(false);

  // Validate with exactly the function the server will run, so the button is
  // never enabled for something the server would reject.
  const valid = useMemo(
    () => sanitizeCustomQuestion(prompt, options) !== null,
    [prompt, options],
  );

  const filled = options.map((o) => o.trim().toLowerCase()).filter(Boolean);
  const hasDuplicates = new Set(filled).size !== filled.length;

  if (!isAuthor) {
    return (
      <>
        <Row $justify="space-between" $wrap $gap="10px">
          <Pill $tone="lavender">{t('question.roundLabel', { round: state.round })}</Pill>
          <CountdownRing fraction={countdown.fraction} seconds={countdown.seconds} />
        </Row>
        <WaitingCard>
          <Stack $gap="12px" $align="center">
            <Dots aria-hidden="true">✏️</Dots>
            <Heading>
              {t('writing.waitingTitle', {
                name: author ? `${author.avatar} ${author.nickname}` : '…',
              })}
            </Heading>
            <Subtle $center>{t('writing.waitingHint')}</Subtle>
          </Stack>
        </WaitingCard>
      </>
    );
  }

  const setOption = (index: number, value: string) => {
    setOptions((current) => current.map((option, i) => (i === index ? value : option)));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid || submitted) return;
    setSubmitted(true);
    actions.submitQuestion(prompt, options);
  };

  return (
    <>
      <Row $justify="space-between" $wrap $gap="10px">
        <Pill $tone="bubblegum">{t('question.roundLabel', { round: state.round })}</Pill>
        <CountdownRing fraction={countdown.fraction} seconds={countdown.seconds} />
      </Row>

      <Pencil aria-hidden="true">✏️</Pencil>

      <Card>
        <form onSubmit={submit}>
          <Stack $gap="20px">
            <Heading>{t('writing.yourTurn')}</Heading>

            <div>
              <Row $justify="space-between" $gap="8px">
                <Label htmlFor="prompt">{t('writing.promptLabel')}</Label>
                <Counter $over={false}>
                  {prompt.length}/{GAME_CONFIG.promptMaxLength}
                </Counter>
              </Row>
              <Input
                id="prompt"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={t('writing.promptPlaceholder')}
                maxLength={GAME_CONFIG.promptMaxLength}
                autoFocus
              />
            </div>

            <Stack $gap="10px">
              <Label as="span">{t('writing.optionsLabel')}</Label>
              {options.map((option, index) => {
                const letter = CHOICES[index] as Choice;
                return (
                  <OptionRow key={index}>
                    <Letter $choice={letter} aria-hidden="true">
                      {letter}
                    </Letter>
                    <Input
                      value={option}
                      onChange={(event) => setOption(index, event.target.value)}
                      placeholder={t('writing.optionPlaceholder', { letter })}
                      maxLength={GAME_CONFIG.optionMaxLength}
                      aria-label={t('writing.optionPlaceholder', { letter })}
                    />
                  </OptionRow>
                );
              })}
            </Stack>

            {hasDuplicates && <Subtle>⚠️ {t('writing.duplicateWarning')}</Subtle>}
            <Subtle>{t('writing.hint')}</Subtle>

            <Button type="submit" $tone="primary" $size="lg" $full disabled={!valid || submitted}>
              🚀 {t('writing.submit')}
            </Button>
          </Stack>
        </form>
      </Card>
    </>
  );
}
